import cardValidator from 'card-validator';
import dayjs from 'dayjs';
import lodashFind from 'lodash/find';
import lodashFindIndex from 'lodash/findIndex';
import lodashGet from 'lodash/get';
import lodashIsEmpty from 'lodash/isEmpty';

import constants from '../Config/constants';

import { capitalize } from './';
import StoreHelper from './Store';

const {
  DATE_DISPLAY_FORMAT,
  DBDATE_FORMAT,
  DBTIME_FORMAT,
  TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
} = constants;

const ASAP = {
  value: 'asap',
  label: 'ASAP',
  date: '',
  time: '',
  canAsap: true,
};

/**
 * Get result of isoDate from storeHours, the return can be store on checkout delivery schedule value
 * @param {String} isoDate - Date ISO format
 * @param {Array} storeHours - An array containing the store hours returned from API https://marketplace-api.stg.pickup.ph/api#/Stores/StoresController_getDates
 * @returns {Object} An object containing information about the store hours:
 * {
 *   ok: boolean // true if found on storeHours, else false
 *   data: {
 *     label: string, // date and time humanize format.
 *     value: string, // ISO format of date & time.
 *     date: string, // Date string DB format.
 *     time: string // Time string DB format.
 *   }
 * }
 */
const getDateFromStoreHours = (isoDate, storeHours) => {
  if (!isoDate) return { ok: false };
  const isoDayjs = dayjs(isoDate);
  const dbDate = isoDayjs.format(DBDATE_FORMAT);
  const dbTime = isoDayjs.format(DBTIME_FORMAT);
  const filterByDate = lodashFind(storeHours, { value: dbDate });
  const timeOptions = filterByDate?.time_options || [];
  const filterByTime = lodashFind(timeOptions, { value: dbTime });
  const isOk = !lodashIsEmpty(filterByTime);
  return {
    ok: isOk,
    data: isOk
      ? {
          value: isoDate,
          label: dayjs(isoDate).format(DATE_DISPLAY_FORMAT),
          date: dbDate,
          time: dbTime,
        }
      : undefined,
  };
};

/**
 * Getr item ids on a error response
 * @param {{errors: string[]}} responseError - raw error response e.g: { errors: ["item_slot_limit.6347be4e8b62830017f9cf9c - reached"] }
 * @returns {string[]} array of item ids
 */
const getItemIdsFromError = (responseError) => {
  const mongoIdRegex = /\b[0-9a-f]{24}\b/i; // from chatgpt
  return responseError?.errors?.map?.((e) => {
    const regRes = e.match(mongoIdRegex);
    return regRes ? regRes[0] : null;
  });
};

/**
 * Get schedule information when select on date picker in cart or checkout page.
 * @returns {Object} An object containing information about the selected schedule:
 *   - label: string, Display label for the reschedule information.
 *   - value: string, Unique value identifier for the reschedule information.
 *   - date: string, Date string representing the rescheduled date.
 *   - time: string, Time string representing the rescheduled time.
 * or if the function encounter error
 * @returns {Object} An object containing information about the selected schedule:
 *   - error: Boolean, Indicates if there is an error in obtaining reschedule information.
 */
const getSelectedSchedule = (selectedDate, dateOptions) => {
  const selectedDatejs = dayjs(selectedDate);
  const currentDateAndTimejs = dayjs();
  const result = {
    label: selectedDatejs.format(DATE_DISPLAY_FORMAT),
    value: selectedDatejs.toISOString(),
    date: selectedDatejs.format(DBDATE_FORMAT),
    time: selectedDatejs.format(DBTIME_FORMAT),
  };
  const isASAP = selectedDatejs.isBefore(currentDateAndTimejs);
  const schedListIndex = lodashFindIndex(dateOptions, {
    date: result.date,
    time: result.time,
  });
  if (isASAP || schedListIndex >= 0) {
    return isASAP ? ASAP : result;
  } else {
    return { error: true };
  }
};

/**
 * Check if can order asap base on storeHours
 * @param {Array} storeHours - An array containing the store hours returned from API https://marketplace-api.stg.pickup.ph/api#/Stores/StoresController_getDates
 * @returns {Boolean} true if can asap, false if cannot
 */
const isCanAsap = (storeHours) => {
  const firstStoreHours = lodashGet(storeHours, '[0].time_options[0].value');
  return firstStoreHours === 'asap_order_time'; // if first value of time_options is asap, it means user can asap order
};

/**
 * Determines if a checkout is allowed based on store hours, off dates, preparation time,
 * and the provided date and time (date filter).
 * @param {Array} storeHours - An array containing the store hours for each day.
 * @param {Array} offDates - An array containing dates when the store is closed. (Optional)
 * @param {number} prepTime - The preparation time required for the order in minutes.
 * @param {string} dateAndTime - The date and time ISO format. (Optional)
 * @returns {Object} An object containing information about the checkout status:
 *   - isClosed: Boolean, true if the store is closed now or the provided date and time.
 *   - isClosing: Boolean, true if the store is closing soon with consideration for preparation time.
 */
const isCanCheckout = (
  storeHours = [],
  offDates = [],
  prepTime = 0,
  dateAndTime,
) => {
  const totalPrepTime = prepTime + constants.PREP_TIME_ALLOWANCE;

  const storeAvailability = StoreHelper.getStoreAvailability(
    storeHours,
    offDates,
    'object',
    dateAndTime,
  );

  const now = dayjs(dateAndTime); // or dateAndTime value
  const closing = dayjs(
    `${now.format(DBDATE_FORMAT)} ${storeAvailability?.closing}`, // same date as now
    `${DBDATE_FORMAT} ${TIME_ONLY_DISPLAY_CAPITAL_FORMAT}`,
  );

  const opening = dayjs(
    `${now.format(DBDATE_FORMAT)} ${storeAvailability?.opening}`, // same date as now
    `${DBDATE_FORMAT} ${TIME_ONLY_DISPLAY_CAPITAL_FORMAT}`,
  );
  const closingWithPreptime = closing.subtract(totalPrepTime, 'minute');

  return {
    isClosed: storeAvailability?.isOpen
      ? now.isSameOrAfter(closing)
      : now.isAfter(opening),
    isClosing: storeAvailability?.isOpen
      ? now.isSameOrAfter(closingWithPreptime)
      : false,
    isOpenLater: now.isSameOrBefore(opening),
  };
};

/**
 * Check if card info is correct
 * @param {*} cardNumber - card number
 * @param {*} expiryMonth - expiration month
 * @param {*} expiryYear - expiration year
 * @param {*} cvv - card cvv
 * @returns {object | void} { isValid: boolean, title: string, message: string}
 */
const validateCard = (cardNumber, expiryMonth, expiryYear, cvv) => {
  const verifyCardNumber = validateCardNumber(
    cardNumber,
    'Please check your card number and try again.',
  );
  const verifyExpiration = validateCardExpiration(
    expiryMonth,
    expiryYear,
    'Please check your expiry month, year and try again.',
  );
  const verifyCvv = validateCardCvv(
    cardNumber,
    cvv,
    'Please check your CVV and try again.',
  );
  if (!verifyCardNumber.isValid) {
    return {
      isValid: false,
      title: 'Card number is invalid',
      message: verifyCardNumber.message,
    };
  } else if (!verifyExpiration.isValid) {
    return {
      isValid: false,
      title: 'Card expiry is invalid',
      message: verifyExpiration.message,
    };
  } else if (!verifyCvv.isValid) {
    return {
      isValid: false,
      title: 'Card CVV is invalid',
      message: verifyCvv.message,
    };
  }
  return { isValid: true };
};

/**
 * Check card cvv if valid
 * @param {*} cardNumber - (string) card number
 * @param {*} cvv - (string) card security number
 * @param {} [errorMsg] - (string) error message
 * @returns {{ isValid: boolean; message?: string }}
 */
const validateCardCvv = (cardNumber, cvv, errorMsg = 'CVV is not valid') => {
  const result = validateCardNumber(cardNumber);
  if (!result.isValid) return { isValid: false, message: errorMsg };
  const cvvSize = result?.card?.code?.size || 0;
  const isValid = cardValidator.cvv(cvv, cvvSize).isValid;
  return {
    isValid,
    message: isValid ? '' : errorMsg,
  };
};

/**
 * Check card cvv if valid
 * @param {*} month - (string) card expiration month
 * @param {*} year - (string) card expiration year
 * @param {} [errorMsg] - (string) error message
 * @returns {{ isValid: boolean; message?: string }}
 */
const validateCardExpiration = (
  month,
  year,
  errorMsg = 'Expiration is not valid',
) => {
  const isValid = cardValidator.expirationDate({ month, year }).isValid;
  return {
    isValid,
    message: isValid ? '' : errorMsg,
  };
};
/**
 * Check card number if valid
 * @param {*} cardNumber - (string) card number
 * @param {} [errorMsg] - (string) error message
 * @returns {{ isValid: boolean; message?: string }}
 */
const validateCardNumber = (
  cardNumber,
  errorMsg = 'Card number is not valid',
) => {
  const availCards = constants.ACCEPTED_PAYMENT_CARDS;
  const result = cardValidator.number(cardNumber);
  const isCardTypeValid = availCards.includes(
    result?.card?.niceType?.toLowerCase?.(),
  );
  if (!isCardTypeValid) {
    return {
      isValid: false,
      message: `We only accept ${availCards
        .map((e) => capitalize(e))
        .join(' and ')} cards at the moment`,
    };
  }
  const isValid = result.isValid;
  return {
    isValid,
    message: isValid ? '' : errorMsg,
  };
};

export default {
  ASAP,
  getDateFromStoreHours,
  getItemIdsFromError,
  getSelectedSchedule,
  isCanAsap,
  isCanCheckout,
  validateCard,
  validateCardCvv,
  validateCardExpiration,
  validateCardNumber,
};
